import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getFormSession,
  postSubmitForm,
} from '../../../../services/forms/formsService';
import useForm from '../useForm';
import Checkboxes from './Options/Checkboxes';
import FileAnswer from './Options/FileAnswer';
import MultipleChoice from './Options/MultipleChoice';
import TextAnswer from './Options/TextAnswer';
import Dropdown from './Options/Dropdown';
import { t } from 'i18next';
import IntegerAnswer from './Options/IntegerAnswer';
import { shownContentUUIDs } from '../../formUtils';

const EMPTY_ANSWER = { file: '', answer: '', choices: [] };

const ViewForm = ({
  UUID,
  onSubmit,
  SubmitMessegeComponent,
  session: sessionProp,
  onSessionChange,
}) => {
  const { uuid } = useParams();
  // С onSessionChange сессией владеет родитель (регистрация хранит её
  // в черновике вкладки), иначе она, как раньше, живёт в localStorage.
  const isSessionControlled = !!onSessionChange;
  const [ownSession, setOwnSession] = useState(null);
  const session = isSessionControlled ? sessionProp : ownSession;
  const { form, contents, fetchForm } = useForm(UUID ?? uuid, session);
  const contentRefs = useRef({});
  // Ответы на все вопросы, а не только на обязательные: по ним решается,
  // какие вопросы показывать (show_if).
  const [answers, setAnswers] = useState({});
  const [notAnswereds, setNotAnswereds] = useState();

  useEffect(() => {
    setAnswers(
      Object.fromEntries(
        (contents ?? []).map((content) => [
          content.uuid,
          {
            file: content?.answer?.file ?? '',
            answer: content?.answer?.answer ?? '',
            choices: content?.answer?.choices ?? [],
          },
        ]),
      ),
    );
  }, [contents]);

  const shown = useMemo(
    () => shownContentUUIDs(contents, answers),
    [contents, answers],
  );

  useEffect(() => {
    if (isSessionControlled) {
      if (sessionProp) return;
      let ignore = false;
      getFormSession(UUID ?? uuid)?.then((res) => {
        if (!ignore) onSessionChange(res.session);
      });
      return () => {
        ignore = true;
      };
    }

    if (localStorage.getItem(`form-session--${UUID ?? uuid}`)) {
      setOwnSession(localStorage.getItem(`form-session--${UUID ?? uuid}`));
    } else {
      getFormSession(UUID ?? uuid)?.then((res) => {
        setOwnSession(res.session);
        localStorage.setItem(`form-session--${UUID ?? uuid}`, res.session);
      });
    }
  }, [UUID, uuid, isSessionControlled, sessionProp, onSessionChange]);

  return (
    <div className='relative mx-auto flex max-h-[93vh] min-h-[93vh] flex-col items-center overflow-y-auto border-t bg-slate-200 p-8 sm:p-12 md:p-16 lg:p-24'>
      <div className='relative flex min-w-full max-w-2xl flex-col gap-4 lg:min-w-[42rem] '>
        {form && form?.remaining_attempts < 1 ? (
          <div
            className={`flex flex-col gap-2 rounded-lg border-t-[0.6rem]
              border-t-violet-700 bg-white p-6`}
          >
            <div
              className='text-3xl'
              dangerouslySetInnerHTML={{
                __html: form?.name,
              }}
            />
            {t('signup.survey_recorded')}
            {SubmitMessegeComponent && <SubmitMessegeComponent />}
          </div>
        ) : (
          <>
            <div
              className={`flex flex-col gap-2 rounded-lg border-t-[0.6rem]
                  border-t-violet-700 bg-white p-6`}
            >
              <div
                className='text-3xl'
                dangerouslySetInnerHTML={{
                  __html: form?.name,
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: form?.descritption, // fix description
                }}
              />
            </div>
            {contents?.map((content, i) => {
              if (!shown.has(content.uuid)) return null;

              const onAnswer = (
                value,
                field = content.test_type === 'FILE'
                  ? 'file'
                  : ['TEXT', 'INTEGER'].includes(content.test_type)
                  ? 'answer'
                  : 'choices',
              ) =>
                setAnswers((prev) => {
                  const next = {
                    ...prev,
                    [content.uuid]: { ...prev[content.uuid], [field]: value },
                  };
                  // Скрытые вопросы теряют ответ — бэк его уже удалил. cleared:
                  // при повторном показе варианты монтируются заново и не
                  // должны взять прежний content.answer.
                  const shownNow = shownContentUUIDs(contents, next);
                  contents.forEach(({ uuid }) => {
                    if (!shownNow.has(uuid)) {
                      next[uuid] = { ...EMPTY_ANSWER, cleared: true };
                    }
                  });
                  return next;
                });
              const optionsContent = answers[content.uuid]?.cleared
                ? {
                    ...content,
                    answer: { answer: '', choices: [], file: null },
                  }
                : content;
              return (
                <div
                  ref={(e) => (contentRefs[content.uuid] = e)}
                  className={`relative flex flex-col gap-2 rounded-lg bg-white p-6 focus:border focus:border-error ${
                    notAnswereds?.includes(content.uuid) &&
                    'border border-error'
                  }`}
                  key={content?.uuid}
                >
                  <div className='flex flex-row items-center gap-2 pb-2'>
                  <div
                    className=''
                    dangerouslySetInnerHTML={{
                      __html: content.name, // fix description
                    }}
                  /> {content.is_required && <span className='text-error font-bold'>*</span>}
                  </div>
                  {content.attachment_img && (
                    <div className='group/image relative my-2 w-fit'>
                      <img
                        alt='attachment'
                        className=': h-fit max-h-64 w-fit'
                        src={content.attachment_img}
                      />
                    </div>
                  )}
                  {content.test_type === 'DROPDOWN' && (
                    <Dropdown
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                  {content.test_type === 'SELECTONE' && (
                    <MultipleChoice
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                  {content.test_type === 'SELECT' && (
                    <Checkboxes
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                  {content.test_type === 'TEXT' && (
                    <TextAnswer
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                  {content.test_type === 'INTEGER' && (
                    <IntegerAnswer
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                  {content.test_type === 'FILE' && (
                    <FileAnswer
                      content={optionsContent}
                      session={session}
                      onAnswer={onAnswer}
                    />
                  )}
                </div>
              );
            })}
            {!!form && (
              <div className='flex flex-row'>
                <button
                  className='rounded bg-violet-700 p-2 px-6 text-sm font-semibold text-slate-200'
                  onClick={() => {
                    // скрытый вопрос не обязателен, даже если отмечен так
                    let notAnswered = contents
                      .filter((c) => c.is_required && shown.has(c.uuid))
                      .map((c) => ({ key: c.uuid, ...answers[c.uuid] }))
                      .filter(
                        (e) => e.answer?.length === 0 && e.choices?.length === 0,
                      );
                    if (!!notAnswered?.length) {
                      setNotAnswereds(notAnswered.map((e) => e.key));

                      contentRefs[notAnswered[0]?.key]?.scrollIntoView();
                      return;
                    }
                    setNotAnswereds([]);
                    postSubmitForm(UUID ?? uuid, session)?.then(() => {
                      fetchForm();
                      if (onSubmit) onSubmit();
                    });
                  }}
                >
                  {t('buttons.finish')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ViewForm;
